import { Link } from "react-router-dom";
import { Hammer, Trees, ArrowRight, Phone } from "lucide-react";
import Seo from "../components/Seo";
import Breadcrumbs from "../components/Breadcrumbs";
import LastUpdated from "../components/LastUpdated";
import { CONCRETE_SERVICES, LANDSCAPE_SERVICES, IMAGES, COMPANY, PAGE_UPDATED, webPageLd, breadcrumbLd } from "../data/site";

/* eslint-disable react/no-unescaped-entities */

function ServiceCard({ s, accent }) {
  return (
    <div
      className="bg-white border border-stone-200 p-7 hover:shadow-lg transition-shadow"
      data-testid={`service-card-${s.name.toLowerCase().replace(/[\s/]/g, "-")}`}
    >
      <div className={`text-[10px] uppercase tracking-[0.22em] font-bold ${accent}`}>
        {s.featured ? "Featured" : "Service"}
      </div>
      <h3 className="mt-3 font-display text-xl font-bold text-stone-900 tracking-tight">
        {s.name}
      </h3>
      <p className="mt-3 text-sm text-stone-600 leading-relaxed">{s.desc}</p>
      <Link
        to="/contact"
        className="mt-5 inline-flex items-center gap-2 text-xs font-bold text-stone-900 hover:text-red-600"
      >
        Request quote <ArrowRight size={14} />
      </Link>
    </div>
  );
}

export default function ServicesPage() {
  return (
    <>
      <Seo
        title="Concrete & Landscape Services | Texan Strong Builds"
        description="Concrete driveways, patios, stamped concrete, repair, foundations, retaining walls, masonry, xeriscape & landscaping — all self-performed in Austin TX."
        canonical={`${window.location.origin}/services`}
        jsonLd={[
          webPageLd({
            name: "Concrete & Landscape Services in Austin TX | Texan Strong Builds",
            path: "/services",
            updated: PAGE_UPDATED.services,
            description: "Concrete driveways, patios, stamped concrete, repair, foundations, masonry, retaining walls, xeriscape and more in Austin TX.",
          }),
          breadcrumbLd([
            { name: "Home", path: "/" },
            { name: "Services", path: "/services" },
          ]),
          {
            "@context": "https://schema.org",
            "@type": "OfferCatalog",
            name: "Concrete & Landscape Services in Austin, TX",
            itemListElement: [...CONCRETE_SERVICES, ...LANDSCAPE_SERVICES].map((s) => ({
              "@type": "Offer",
              itemOffered: {
                "@type": "Service",
                name: s.name,
                description: s.desc,
                areaServed: "Austin, TX",
                provider: { "@type": "GeneralContractor", name: "Texan Strong Builds" },
              },
            })),
          },
        ]}
      />

      {/* Page header */}
      <section className="bg-stone-900 text-white pt-32 pb-20 lg:pt-40 lg:pb-28">
        <div className="max-w-7xl mx-auto px-5 lg:px-8 grid lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-7">
            <Breadcrumbs dark items={[{ name: "Home", path: "/" }, { name: "Services", path: "/services" }]} />
            <div className="text-xs uppercase tracking-[0.22em] font-bold text-red-500">
              Services
            </div>
            <h1 className="mt-4 font-display text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter leading-[0.95]">
              Everything we build, pour, plant or repair in Austin.
            </h1>
          </div>
          <div className="lg:col-span-5">
            <p className="text-stone-300 text-lg leading-relaxed">
              Two specialties, dozens of services, one local Austin, TX team. Every job is
              self-performed by Texan Strong Builds crews — no subcontractor mystery, no quality
              drop-off.
            </p>
            <LastUpdated dark date={PAGE_UPDATED.services} className="mt-5" />
          </div>
        </div>
      </section>

      {/* Concrete services */}
      <section className="bg-stone-50 py-20 md:py-28 border-y border-stone-200" data-testid="concrete-services-grid">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="flex items-center gap-3 text-xs uppercase tracking-[0.22em] font-bold text-red-600">
            <Hammer size={16} /> Concrete
          </div>
          <h2 className="mt-3 font-display text-4xl sm:text-5xl font-black tracking-tighter text-stone-900 leading-[1.02]">
            Concrete services in Austin.
          </h2>
          <p className="mt-4 text-stone-600 text-lg max-w-2xl">
            Texan Strong Builds pours concrete engineered for Austin clay soil, finished for the
            Texas sun, and reinforced for the long haul.
          </p>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {CONCRETE_SERVICES.map((s) => (
              <ServiceCard key={s.name} s={s} accent="text-red-600" />
            ))}
          </div>
        </div>
      </section>

      {/* Landscape services */}
      <section className="bg-white py-20 md:py-28" data-testid="landscape-services-grid">
        <div className="max-w-7xl mx-auto px-5 lg:px-8">
          <div className="flex items-center gap-3 text-xs uppercase tracking-[0.22em] font-bold text-emerald-700">
            <Trees size={16} /> Landscape
          </div>
          <h2 className="mt-3 font-display text-4xl sm:text-5xl font-black tracking-tighter text-stone-900 leading-[1.02]">
            Landscape & hardscape services.
          </h2>
          <p className="mt-4 text-stone-600 text-lg max-w-2xl">
            Texan Strong Builds installs xeriscape design, irrigation, sod, fire pits, outdoor
            lighting, decks and full hardscape — designed for the way Austin, TX really lives outside.
          </p>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5">
            {LANDSCAPE_SERVICES.map((s) => (
              <ServiceCard key={s.name} s={s} accent={s.featured ? "text-red-600" : "text-emerald-700"} />
            ))}
          </div>
        </div>
      </section>

      {/* CTA strip */}
      <section className="bg-stone-900 text-white py-20">
        <div className="max-w-5xl mx-auto px-5 lg:px-8 text-center">
          <h3 className="font-display text-4xl sm:text-5xl font-black tracking-tighter leading-[1.02]">
            Not sure which service you need?
          </h3>
          <p className="mt-4 text-stone-300 text-lg">
            Tell us what you're trying to build. We'll come measure, draw it up, and write you a free estimate.
          </p>
          <div className="mt-8 flex flex-col sm:flex-row justify-center gap-3">
            <Link to="/contact" className="btn-primary" data-testid="services-cta-estimate">
              Get a Free Estimate <ArrowRight size={18} />
            </Link>
            <a href={COMPANY.phoneTel} className="btn-ghost-dark" data-testid="services-cta-call">
              <Phone size={18} /> Call {COMPANY.phone}
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
